import React from 'react';
import { PolicyMode } from '../services/softwareComplianceService';

interface TenantGroup {
  tenantGroupId: string;
  groupName: string;
  isDefault: boolean;
}

interface SoftwarePolicyModalProps {
  show: boolean;
  policyMode: PolicyMode;
  selectedItemCount: number;
  selectAllGroups: boolean;
  tenantGroups: TenantGroup[];
  selectedGroups: Set<string>;
  submittingPolicy: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onSelectAllGroupsChange: (checked: boolean) => void;
  onGroupToggle: (groupId: string, checked: boolean) => void;
}

const SoftwarePolicyModal: React.FC<SoftwarePolicyModalProps> = ({
  show,
  policyMode,
  selectedItemCount,
  selectAllGroups,
  tenantGroups,
  selectedGroups,
  submittingPolicy,
  onClose,
  onSubmit,
  onSelectAllGroupsChange,
  onGroupToggle,
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity">
      <div className="w-full max-w-2xl rounded-xl bg-[#1c1c1c] p-6 text-white shadow-2xl border border-gray-700 mx-4">
        <div className="flex items-center justify-between border-b border-gray-700 pb-3 mb-4">
          <h2 className="text-lg font-semibold">Apply Software Access Policy</h2>
        </div>

        <p className="text-sm text-gray-300 mb-6">
          You are about to set <span className="font-semibold">{policyMode}</span> for{' '}
          {selectedItemCount === 1 ? 'this software item' : `${selectedItemCount} software items`} across the selected groups.
        </p>
        {policyMode === PolicyMode.Blacklist && (
          <div className="flex gap-3 bg-orange-950/40 border border-l-4 border-orange-800 border-l-orange-500 rounded-lg p-3 mb-5">
            <svg className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-orange-400 mb-1">Keep in mind - Blacklisting may affect associated software</p>
              <p className="text-xs text-orange-200/70 leading-relaxed">
                Blocking the application may also impact other software from the same publisher that relies on shared services or
                components. Apps sharing those dependencies could behave unpredictably or fail to launch — even if they are not explicitly
                blacklisted.
              </p>
            </div>
          </div>
        )}
        <div className="mb-4">
          <label className="flex items-center cursor-pointer text-sm text-gray-200">
            <input
              type="checkbox"
              checked={selectAllGroups}
              onChange={(e) => onSelectAllGroupsChange(e.target.checked)}
              className="sr-only peer"
            />
            <span className="inline-block h-[18px] w-[18px] rounded-[4px] border-2 border-[#444] bg-transparent transition-colors peer-checked:border-[#00FF00] peer-checked:bg-[#00FF00]" />
            <span className="ml-3 font-medium">Select all groups</span>
          </label>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-400 mb-2">Choose Target Groups:</p>
          <div className="max-h-48 overflow-y-auto border border-gray-700 rounded-lg p-2 bg-[#181818]">
            {tenantGroups.length === 0 ? (
              <p className="text-gray-500 text-sm">No active groups found</p>
            ) : (
              tenantGroups.map((group) => (
                <label key={group.tenantGroupId} className="flex items-center py-2 cursor-pointer hover:bg-gray-800 px-2 rounded">
                  <input
                    type="checkbox"
                    checked={selectedGroups.has(group.tenantGroupId)}
                    onChange={(e) => onGroupToggle(group.tenantGroupId, e.target.checked)}
                    className="sr-only peer"
                  />
                  <span className="inline-block h-4 w-4 rounded border border-gray-500 bg-transparent transition-colors peer-checked:bg-[#08a532] peer-checked:border-[#08a532]" />
                  <span className="ml-2 text-white text-sm">{group.groupName}</span>
                  {group.isDefault && <span className="ml-2 text-xs text-gray-500">(Default)</span>}
                </label>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            className="px-5 py-2 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-700 transition-all"
            onClick={onClose}
            disabled={submittingPolicy}
          >
            Cancel
          </button>
          <button
            className={`px-5 py-2 rounded-full font-medium transition-all ${
              policyMode === PolicyMode.Blacklist ? 'bg-red-700 hover:bg-red-800 text-white' : 'bg-green-700 hover:bg-green-800 text-white'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={onSubmit}
            disabled={submittingPolicy || selectedGroups.size === 0}
          >
            {submittingPolicy ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SoftwarePolicyModal;
